import { z } from "zod";

/**
 * The CDT release a merchflow's store pogs were generated against: the COMPILED
 * four-level contract, not the seven-level working spine `./draft.ts` returns.
 *
 * A store pog's CDT columns ARE the contract's levels in order — `variant` holds
 * `__cdt0`, `cdt1` holds `__cdt1`, `cdt2` holds `__cdt2`, `cdt3` holds `__cdt3` —
 * so `spine` is those levels already named by the pog column they land in, each
 * with the orientation the merchant gave it, in the studio's words (horizontal =
 * an x-stripe, vertical = a band across the bays). It is what the Aesthetics
 * score needs to grade a store pog's blocking in two dimensions:
 * `pogAestheticsScore(pog, {}, { spine })`.
 *
 * Without `storePogId` this is the newest release for the merchflow. With it,
 * the release that pog was generated against: the one its generation run pinned
 * (`store_pog_trigger.cdt_release_id`), else the newest release of the decision
 * tree the pog snapshotted (`output_store_pog.effective_decision_tree_id`, which
 * a duplicated merchflow copies from its source), else the pog's own base
 * pog's newest release; never another wall's.
 * 404 when none of those exists, or when the pog is not on this merchflow.
 */
export const RouteGetMerchflows_merchflowId_CdtRelease = "/merchflows/:merchflowId/cdt/release";

export const ParamsMerchflowCdtReleaseSchema = z.object({
  merchflowId: z.coerce.number().int().positive(),
});

export const QueryGetMerchflowCdtReleaseSchema = z.object({
  storePogId: z.coerce.number().int().positive().optional(),
});

/** The store pog column each contract depth lands in. */
export const CDT_RELEASE_POG_COLUMNS = ["variant", "cdt1", "cdt2", "cdt3"] as const;

export const CdtReleaseSpineLevelSchema = z.object({
  attr: z.enum(CDT_RELEASE_POG_COLUMNS),
  label: z.string(),
  orient: z.enum(["vertical", "horizontal"]),
});

/** One level of the release's contract, as stored (`contract.levels[i]`). */
export const CdtReleaseContractLevelSchema = z.looseObject({
  attr: z.string(),
  label: z.string().optional(),
  orient: z.string().optional(),
  sourceAttrs: z.array(z.string()).optional(),
});

export const ResponseGetMerchflowCdtReleaseSchema = z.object({
  id: z.number(),
  /** The merchflow that shipped the release; not the requested one when it came via a copied pog. */
  merchflowId: z.number(),
  basePogId: z.number().nullable(),
  /** `toISOString()` of `released_at`, as the draft GET's `updatedAt`. */
  releasedAt: z.string(),
  /** How the release was found: see the route's note. */
  resolvedBy: z.enum([
    "storePogTrigger",
    "effectiveDecisionTree",
    "latestForBasePog",
    "latestForMerchflow",
  ]),
  spine: z.array(CdtReleaseSpineLevelSchema),
  contractLevels: z.array(CdtReleaseContractLevelSchema),
  /** The base planogram's length unit (its PSA project's): labels only. Absent = inches. */
  lengthUnit: z.enum(["in", "cm"]).optional(),
});

export type ParamsMerchflowCdtRelease = z.infer<typeof ParamsMerchflowCdtReleaseSchema>;
export type QueryGetMerchflowCdtRelease = z.infer<typeof QueryGetMerchflowCdtReleaseSchema>;
export type CdtReleaseSpineLevel = z.infer<typeof CdtReleaseSpineLevelSchema>;
export type CdtReleaseContractLevel = z.infer<typeof CdtReleaseContractLevelSchema>;
export type ResponseGetMerchflowCdtRelease = z.infer<typeof ResponseGetMerchflowCdtReleaseSchema>;
